import type { Tab, TabItem } from '../types';

export const calculateUserTotal = (tab: Tab, userId: string): number => {
    if (!userId) return 0;

    // 1. Calculate User's Share of Items
    const userItems = tab.items.filter(item => item.claimedBy.includes(userId));

    // Helper to get user's cost for an item
    const getUserCost = (item: TabItem) => {
        const qty = item.quantity ?? 1;

        if (qty > 1) {
            // Itemized Mode: User pays for their specific units
            // claimedBy contains one entry per unit claimed
            const unitPrice = item.price / qty;
            const userClaimCount = item.claimedBy.filter(uid => uid === userId).length;
            return unitPrice * userClaimCount;
        } else {
            // Shared Mode: Split equally among all claimers
            // claimedBy contains unique user IDs
            return item.price / item.claimedBy.length;
        }
    };

    const userSubtotal = userItems.reduce((sum, item) => sum + getUserCost(item), 0);

    // 2. Calculate User's Share of Discounts
    let userDiscountTotal = 0;
    if (tab.discounts && tab.discounts.length > 0) {
        tab.discounts.forEach(discount => {
            // Find eligible items for this discount (globally and for user)
            const isApplicable = (item: TabItem) => !discount.category || item.category === discount.category;

            const totalEligibleAmount = tab.items
                .filter(isApplicable)
                .reduce((sum, item) => sum + item.price, 0);

            const userEligibleAmount = userItems
                .filter(isApplicable)
                .reduce((sum, item) => sum + getUserCost(item), 0);

            if (totalEligibleAmount > 0) {
                // Calculate discount amount
                let discountAmount = 0;
                if (discount.type === 'fixed') {
                    discountAmount = discount.value;
                } else {
                    discountAmount = totalEligibleAmount * (discount.value / 100);
                }

                // User's share of the discount
                const userShareFraction = userEligibleAmount / totalEligibleAmount;
                userDiscountTotal += discountAmount * userShareFraction;
            }
        });
    }

    // 3. Service Charge & Tip (Based on User's Gross Subtotal)
    const tipAmount = userSubtotal * (tab.tipPercentage / 100);
    const serviceAmount = userSubtotal * (tab.serviceChargePercentage / 100);

    return Math.max(0, userSubtotal + tipAmount + serviceAmount - userDiscountTotal);
};

export const calculateBillTotal = (tab: Tab): number => {

    const subtotal = tab.items.reduce((sum, item) => sum + item.price, 0);

    let totalDiscount = 0;
    if (tab.discounts) {
        tab.discounts.forEach(d => {
            let eligibleAmount = 0;
            if (d.category) {
                eligibleAmount = tab.items
                    .filter(i => i.category === d.category)
                    .reduce((sum, i) => sum + i.price, 0);
            } else {
                eligibleAmount = subtotal;
            }

            if (d.type === 'fixed') {
                totalDiscount += d.value;
            } else {
                totalDiscount += eligibleAmount * (d.value / 100);
            }
        });
    }

    const tipAmount = subtotal * (tab.tipPercentage / 100);
    const serviceAmount = subtotal * (tab.serviceChargePercentage / 100);

    return Math.max(0, subtotal + tipAmount + serviceAmount - totalDiscount);
};

export const getTabStatus = (tab: Tab): 'complete' | 'in_progress' | 'not_started' | 'all_paid' => {
    if (tab.isComplete) return 'complete';

    const manualMembers = tab.manualMembers ?? [];
    // Payers = everyone EXCEPT the owner
    const payers = [...tab.members, ...manualMembers.map(m => m.id)].filter(id => id !== tab.ownerId);

    if (payers.length === 0) return 'not_started';

    const completed = tab.completedMembers ?? [];
    const paidCount = payers.filter(id => completed.includes(id)).length;

    if (paidCount === 0) return 'not_started';

    if (paidCount === payers.length) return 'all_paid';

    return 'in_progress';
};
